import React, { useState, useEffect } from 'react';
import {
    Container,
    Header,
    Table,
    Button,
    Modal,
    Form,
    Message,
    Icon,
    Loader,
    Segment
} from 'semantic-ui-react';
import { fetchWithAuth, getCSRFToken } from '../auth';
import { isICCSupport, isReadOnly } from '../auth/Auth';

export const ContactsHub = () => {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [currentContact, setCurrentContact] = useState(null);
    const [formData, setFormData] = useState({
        contactType: '',
        information: '',
        displayOrder: 0
    });
    const [formErrors, setFormErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const canEdit = isICCSupport() && !isReadOnly();

    useEffect(() => {
        loadContacts();
    }, []);

    const loadContacts = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await fetchWithAuth(
                process.env.REACT_APP_ICC_TOOL_API_HOST + 'contact-info/',
                {
                    method: 'GET',
                    credentials: 'include'
                }
            );

            if (!response.ok) {
                throw new Error('Failed to load contacts');
            }

            const data = await response.json();
            setContacts(data);
        } catch (err) {
            setError('Failed to load contact information. Please try again.');
            console.error('Error loading contacts:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (contact) => {
        setCurrentContact(contact);
        setFormData({
            contactType: contact.contactType,
            information: contact.information,
            displayOrder: contact.displayOrder
        });
        setFormErrors({});
        setEditModalOpen(true);
    };

    const handleAdd = () => {
        setCurrentContact(null);
        setFormData({
            contactType: '',
            information: '',
            displayOrder: contacts.length
        });
        setFormErrors({});
        setEditModalOpen(true);
    };

    const handleDelete = (contact) => {
        setCurrentContact(contact);
        setDeleteModalOpen(true);
    };

    const validateForm = () => {
        const errors = {};
        // Contact type is optional to allow blank rows
        if (!formData.information || formData.information.trim() === '') {
            errors.information = 'Information is required';
        }
        if (formData.displayOrder < 0) {
            errors.displayOrder = 'Display order must be 0 or greater';
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        setSaving(true);
        setError('');

        try {
            const url = currentContact
                ? `${process.env.REACT_APP_ICC_TOOL_API_HOST}contact-info/${currentContact.id}/`
                : `${process.env.REACT_APP_ICC_TOOL_API_HOST}contact-info/`;

            const method = currentContact ? 'PATCH' : 'POST';

            const response = await fetchWithAuth(url, {
                method: method,
                headers: {
                    'X-CSRFToken': getCSRFToken(),
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to save contact');
            }

            await loadContacts();
            setEditModalOpen(false);
            setCurrentContact(null);
        } catch (err) {
            setError(err.message || 'Failed to save contact. Please try again.');
            console.error('Error saving contact:', err);
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (!currentContact) return;

        setSaving(true);
        setError('');

        try {
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_ICC_TOOL_API_HOST}contact-info/${currentContact.id}/`,
                {
                    method: 'DELETE',
                    headers: {
                        'X-CSRFToken': getCSRFToken()
                    },
                    credentials: 'include'
                }
            );

            if (!response.ok) {
                throw new Error('Failed to delete contact');
            }

            await loadContacts();
            setDeleteModalOpen(false);
            setCurrentContact(null);
        } catch (err) {
            setError('Failed to delete contact. Please try again.');
            console.error('Error deleting contact:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleFormChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error for this field when user starts typing
        if (formErrors[field]) {
            setFormErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    const renderContactInformation = (info) => {
        // Check if it's a link (starts with http:// or https://)
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/g;
        
        // Split by newlines to preserve line breaks
        const lines = info.split('\n');
        
        return lines.map((line, lineIndex) => {
            // Replace URLs with clickable links
            let parts = line.split(urlRegex);
            parts = parts.map((part, index) => {
                if (urlRegex.test(part)) {
                    return <a key={index} href={part} target="_blank" rel="noopener noreferrer" style={{ color: '#4183c4' }}>{part}</a>;
                }
                // Replace emails with mailto links
                let emailParts = part.split(emailRegex);
                return emailParts.map((emailPart, emailIndex) => {
                    if (emailRegex.test(emailPart)) {
                        return <a key={`${index}-${emailIndex}`} href={`mailto:${emailPart}`} style={{ color: '#4183c4' }}>{emailPart}</a>;
                    }
                    return emailPart;
                });
            });
            
            return (
                <React.Fragment key={lineIndex}>
                    {parts}
                    {lineIndex < lines.length - 1 && <br />}
                </React.Fragment>
            );
        });
    };

    if (loading) {
        return (
            <Container style={{ marginTop: '2rem', textAlign: 'center' }}>
                <Loader active inline='centered' size='large'>Loading contacts...</Loader>
            </Container>
        );
    }

    return (
        <Container style={{ marginTop: '2rem', marginBottom: '2rem', maxWidth: '1200px' }}>
            {canEdit && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                    <Button
                        primary
                        icon
                        labelPosition='left'
                        onClick={handleAdd}
                    >
                        <Icon name='plus' />
                        Add Contact
                    </Button>
                </div>
            )}

            {error && (
                <Message negative onDismiss={() => setError('')}>
                    <Message.Header>Error</Message.Header>
                    <p>{error}</p>
                </Message>
            )}

            <Segment style={{ padding: 0 }}>
                <div style={{ 
                    padding: '1rem 1.5rem', 
                    backgroundColor: '#2c2c2c', 
                    color: 'white',
                    fontSize: '1.1rem',
                    borderBottom: '2px solid #444'
                }}>
                    Add contact information for below departments:
                </div>

                {contacts.length === 0 ? (
                    <div style={{ padding: '3rem', textAlign: 'center' }}>
                        <Header icon>
                            <Icon name='address book outline' />
                            No contact information available
                        </Header>
                        {canEdit && (
                            <Button primary onClick={handleAdd}>
                                Add First Contact
                            </Button>
                        )}
                    </div>
                ) : (
                    <>
                        <Table celled striped style={{ margin: 0, border: 'none' }}>
                            <Table.Header>
                                <Table.Row style={{ backgroundColor: '#3a3a3a' }}>
                                    <Table.HeaderCell 
                                        width={5} 
                                        style={{ 
                                            color: '#aaa',
                                            fontWeight: 'normal',
                                            fontSize: '0.95rem',
                                            borderLeft: 'none'
                                        }}
                                    >
                                        Contact
                                    </Table.HeaderCell>
                                    <Table.HeaderCell 
                                        style={{ 
                                            color: '#aaa',
                                            fontWeight: 'normal',
                                            fontSize: '0.95rem'
                                        }}
                                    >
                                        Information
                                    </Table.HeaderCell>
                                    {canEdit && (
                                        <Table.HeaderCell 
                                            width={2} 
                                            textAlign='center'
                                            style={{ 
                                                color: '#aaa',
                                                fontWeight: 'normal',
                                                fontSize: '0.95rem',
                                                borderRight: 'none'
                                            }}
                                        >
                                            Actions
                                        </Table.HeaderCell>
                                    )}
                                </Table.Row>
                            </Table.Header>

                            <Table.Body>
                                {contacts.map((contact) => (
                                    <Table.Row key={contact.id}>
                                        <Table.Cell style={{ borderLeft: 'none' }}>
                                            {contact.contactType || ''}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {renderContactInformation(contact.information)}
                                        </Table.Cell>
                                        {canEdit && (
                                            <Table.Cell textAlign='center' style={{ borderRight: 'none' }}>
                                                <Button
                                                    icon
                                                    size='small'
                                                    onClick={() => handleEdit(contact)}
                                                    title='Edit'
                                                >
                                                    <Icon name='edit' />
                                                </Button>
                                                <Button
                                                    icon
                                                    size='small'
                                                    color='red'
                                                    onClick={() => handleDelete(contact)}
                                                    title='Delete'
                                                >
                                                    <Icon name='trash' />
                                                </Button>
                                            </Table.Cell>
                                        )}
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </>
                )}
            </Segment>

            {/* Edit/Add Modal */}
            <Modal
                open={editModalOpen}
                onClose={() => !saving && setEditModalOpen(false)}
                size='small'
            >
                <Modal.Header>{currentContact ? 'Edit Contact' : 'Add New Contact'}</Modal.Header>
                <Modal.Content>
                    <Form>
                        <Form.Input
                            label='Contact Type'
                            placeholder='e.g., First Level Contact, ICC Support Level 1, or leave blank'
                            value={formData.contactType}
                            onChange={(e, { value }) => handleFormChange('contactType', value)}
                            error={formErrors.contactType}
                        />
                        <Form.TextArea
                            label='Information'
                            placeholder='e.g., Direct Manager, Market Analyst, Spokane: Ethan Colvard'
                            value={formData.information}
                            onChange={(e, { value }) => handleFormChange('information', value)}
                            error={formErrors.information}
                            rows={3}
                            required
                        />
                        <Form.Input
                            label='Display Order'
                            type='number'
                            value={formData.displayOrder}
                            onChange={(e, { value }) => handleFormChange('displayOrder', parseInt(value) || 0)}
                            error={formErrors.displayOrder}
                            min={0}
                        />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setEditModalOpen(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        primary
                        onClick={handleSave}
                        loading={saving}
                        disabled={saving}
                    >
                        {currentContact ? 'Update' : 'Create'}
                    </Button>
                </Modal.Actions>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                open={deleteModalOpen}
                onClose={() => !saving && setDeleteModalOpen(false)}
                size='tiny'
            >
                <Modal.Header>Confirm Delete</Modal.Header>
                <Modal.Content>
                    <p>Are you sure you want to delete this contact information?</p>
                    {currentContact && (
                        <p><strong>{currentContact.contactType || currentContact.information}</strong></p>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setDeleteModalOpen(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        color='red'
                        onClick={confirmDelete}
                        loading={saving}
                        disabled={saving}
                    >
                        Delete
                    </Button>
                </Modal.Actions>
            </Modal>
        </Container>
    );
};
